import { TablePage, Dialog, View } from 'web-mojo';
import { AccessWindowList } from '@portal/models/AccessWindow.js';

export default class AccessWindowsPage extends TablePage {
    constructor(options = {}) {
        super({
            title: 'Access Windows',
            icon: 'bi-key-fill',
            pageName: 'access_windows',
            route: 'group/access',
            permissions: ['manage_routes', 'manage_cam_devices', 'manage_group'],
            requiresGroup: true,

            Collection: AccessWindowList,

            defaultQuery: {
                group: () => app.activeGroup?.id,
                sort: '-valid_from',
                size: 100,
            },

            columns: [
                {
                    label: 'Status',
                    key: 'status',
                    formatter: "badge:pending=warning,active=success,expiring_soon=warning,expired=secondary,revoked=danger",
                    filter: {
                        type: 'select',
                        options: [
                            { label: 'Pending',       value: 'pending' },
                            { label: 'Active',        value: 'active' },
                            { label: 'Expiring Soon', value: 'expiring_soon' },
                            { label: 'Expired',       value: 'expired' },
                            { label: 'Revoked',       value: 'revoked' },
                        ]
                    }
                },
                {
                    label: 'Device',
                    key: 'device.name',
                    formatter: "default('—')",
                },
                {
                    label: 'Operator',
                    key: 'operator.display_name',
                    formatter: "default('—')",
                },
                {
                    label: 'Type',
                    key: 'grant_type',
                    filter: {
                        type: 'select',
                        options: [
                            { label: 'Auto (route)', value: 'auto' },
                            { label: 'Manual',       value: 'manual' },
                        ]
                    },
                    visibility: 'md',
                },
                {
                    label: 'Valid From',
                    key: 'valid_from',
                    formatter: 'datetime',
                    sortable: true,
                    visibility: 'md',
                },
                {
                    label: 'Valid Until',
                    key: 'valid_until',
                    formatter: 'datetime',
                    sortable: true,
                    visibility: 'md',
                },
                {
                    label: 'Route',
                    key: 'route.template.name',
                    formatter: "default('—')",
                    visibility: 'lg',
                },
            ],

            selectable: true,
            batchActions: [
                { label: "Revoke", icon: "bi bi-x-circle", action: "revoke", className: "text-danger" },
                { label: "Export", icon: "bi bi-download", action: "export" },
            ],
            searchable: false,
            sortable: true,
            filterable: true,
            paginated: true,

            showRefresh: true,
            showAdd: true,
            addLabel: 'Manual Window',
            showExport: false,

            tableOptions: {
                striped: false,
                bordered: false,
                hover: true,
                responsive: true,
            },

            contextMenu: [
                { icon: 'bi-clock-history', label: 'Extend',  action: 'extend-window' },
                { icon: 'bi-x-circle', label: 'Revoke',  action: 'revoke-window', className: 'text-danger' },
            ],

            ...options,
        });
    }

    async onActionBatchRevoke() {
        const selected = this.tableView.getSelectedItems();
        if (!selected?.length) return;
        const confirmed = await Dialog.confirm({
            title: `Revoke ${selected.length} access window${selected.length > 1 ? 's' : ''}`,
            message: 'Revoke selected access windows? Operators will lose access immediately.',
            confirmText: 'Revoke',
            confirmClass: 'btn-danger',
        });
        if (!confirmed) return;
        await Promise.all(selected.map(item => item.model.save({
            revoke: { reason: 'Batch revocation by planner' },
        })));
        app.toast.success(`Revoked ${selected.length} access window${selected.length > 1 ? 's' : ''}`);
        this.tableView.collection.fetch();
    }

    async onActionBatchExport() {
        this.tableView.collection.export();
    }

    // ── Toolbar ───────────────────────────────────────────────────────────────

    onActionAdd() {
        const form = new ManualWindowForm();
        Dialog.showDialog({
            title: 'Create Manual Access Window',
            body: form,
            size: 'md',
            buttons: [
                { text: 'Cancel', class: 'btn-secondary', dismiss: true },
                { text: 'Create Window', class: 'btn-primary',
                  handler: async () => {
                      const ok = await form.onActionSave();
                      if (ok) this.collection.fetch();
                      return ok;
                  }
                },
            ],
        });
    }

    // ── Row actions ───────────────────────────────────────────────────────────

    async onActionExtendWindow(event, el) {
        const id = el.getAttribute('data-id');
        const form = new ExtendWindowForm({ windowId: id });
        Dialog.showDialog({
            title: 'Extend Access Window',
            body: form,
            size: 'sm',
            buttons: [
                { text: 'Cancel', class: 'btn-secondary', dismiss: true },
                { text: 'Extend', class: 'btn-primary',
                  handler: async () => {
                      const ok = await form.onActionSave();
                      if (ok) this.collection.fetch();
                      return ok;
                  }
                },
            ],
        });
    }

    async onActionRevokeWindow(event, el) {
        const id = el.getAttribute('data-id');
        const window = this.collection.get(id);
        if (!window) return;
        const confirmed = await Dialog.confirm({
            title: 'Revoke Access Window',
            message: 'Revoke this access window immediately? The operator will lose access.',
            confirmText: 'Revoke',
            confirmClass: 'btn-danger',
        });
        if (!confirmed) return;

        const response = await window.save({
            revoke: { reason: 'Manual revocation by planner' },
        });
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to revoke window');
            return;
        }
        app.toast.success('Access window revoked');
        this.tableView.collection.fetch();
    }
}

// ── Forms ─────────────────────────────────────────────────────────────────────

class ManualWindowForm extends View {
    async onActionSave() {
        const deviceId = this.element.querySelector('[name=device_id]')?.value.trim();
        const operatorId = this.element.querySelector('[name=operator_id]')?.value.trim();
        const validFrom = this.element.querySelector('[name=valid_from]')?.value;
        const validUntil = this.element.querySelector('[name=valid_until]')?.value;

        if (!deviceId || !operatorId || !validFrom || !validUntil) {
            app.toast.warning('All fields are required');
            return false;
        }

        const response = await app.api.post('/api/camaccess/access_window', {
            device: parseInt(deviceId),
            operator: parseInt(operatorId),
            valid_from: new Date(validFrom).toISOString(),
            valid_until: new Date(validUntil).toISOString(),
            grant_type: 'manual',
        });
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to create window');
            return false;
        }
        app.toast.success('Access window created');
        this.emit('saved', response.data.data);
        return true;
    }

    get template() {
        const now = new Date();
        const later = new Date(now.getTime() + 8 * 60 * 60 * 1000);
        const toLocal = d => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

        return `
            <div class="p-3">
                <div class="mb-3">
                    <label class="form-label">Device ID <span class="text-danger">*</span></label>
                    <input type="number" class="form-control" name="device_id" placeholder="Device ID">
                </div>
                <div class="mb-3">
                    <label class="form-label">Operator ID <span class="text-danger">*</span></label>
                    <input type="number" class="form-control" name="operator_id" placeholder="Operator ID">
                </div>
                <div class="mb-3">
                    <label class="form-label">Valid From <span class="text-danger">*</span></label>
                    <input type="datetime-local" class="form-control" name="valid_from" value="${toLocal(now)}">
                </div>
                <div class="mb-4">
                    <label class="form-label">Valid Until <span class="text-danger">*</span></label>
                    <input type="datetime-local" class="form-control" name="valid_until" value="${toLocal(later)}">
                </div>
            </div>
        `;
    }

    set template(v) { this._t = v; }
}

class ExtendWindowForm extends View {
    constructor({ windowId }) {
        super();
        this.windowId = windowId;
    }

    async onActionSave() {
        const validUntil = this.element.querySelector('[name=valid_until]')?.value;
        if (!validUntil) { app.toast.warning('Please pick a date/time'); return false; }

        const response = await app.api.patch(`/api/camaccess/access_window/${this.windowId}`, {
            action: 'extend',
            valid_until: new Date(validUntil).toISOString(),
        });
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to extend window');
            return false;
        }
        app.toast.success('Window extended');
        return true;
    }

    get template() {
        const later = new Date(Date.now() + 2 * 60 * 60 * 1000);
        const toLocal = d => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

        return `
            <div class="p-3">
                <div class="mb-4">
                    <label class="form-label">Extend Until</label>
                    <input type="datetime-local" class="form-control" name="valid_until" value="${toLocal(later)}">
                </div>
            </div>
        `;
    }

    set template(v) { this._t = v; }
}
